import { DownArrowIcon } from 'assets/icons';
import cn from 'classnames';
import { FormikErrors, FormikTouched } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputSelectList } from 'shared/components/form/inputSelectList';
import InputText from 'shared/components/form/inputText';

import './styles.scss';

interface InputSelectProps {
	label: string;
	placeholder?: string;
	errors: FormikErrors<{ [field: string]: any }>;
	touched: FormikTouched<{}>;
	name: string;
	values: any;
	selectItems: any;
	isFieldValueValid: (field: string, errors: any, touched: {}) => boolean;
	setFieldValue: (field: string, val: any) => void;
	setFieldTouched: (name: string, bool: boolean) => void;
	setFieldError: (field: string, val: any) => void;
	className?: string;
	onBlur: any;
	cyTestRef?: string;
	cySelectionTestRef?: string;
	cyErrorRef?: string;
}

export const InputSelect = ({
	name,
	label,
	isFieldValueValid,
	errors,
	touched,
	setFieldValue,
	values,
	setFieldTouched,
	selectItems,
	className,
	onBlur,
	cyTestRef,
	cySelectionTestRef,
	cyErrorRef,
}: InputSelectProps) => {
	const { t } = useTranslation();
	const isError = isFieldValueValid(name, errors, touched);
	const [open, setOpen] = useState(false);

	const handleClick = () => {
		setOpen(!open);
	};

	const sortAlphabetically = useMemo(() => {
		const sortedItems = [...selectItems];
		sortedItems.sort((a: any, b: any) => t(a).localeCompare(t(b)));
		return sortedItems;
	}, [selectItems, t]);

	return (
		<>
			<div
				className={cn('inputSelect', {
					'inputSelect--error': isError,
				})}
				onClick={() => {
					setFieldTouched(name, true);
					handleClick();
				}}
			>
				<InputText
					label={label}
					isFieldValueValid={isFieldValueValid}
					errors={errors}
					touched={touched}
					onBlur={onBlur}
					setFieldValue={setFieldValue}
					name={name}
					values={values}
					isPasswordInput={false}
					inputProps={{ readOnly: true, 'data-cy': cyTestRef }}
					formatValue={t(values[name]) ?? ''}
					shrinkInput={!!values[name]}
					cyTestRef={cyTestRef}
					cyErrorRef={cyErrorRef}
				/>
				<div className="inputSelect__label">
					<DownArrowIcon className={`inputSelect__icon ${isError ? 'fill--error' : 'fill--primary'}`} />
				</div>
			</div>

			{open && (
				<InputSelectList
					label={label}
					name={name}
					list={sortAlphabetically}
					handleClick={handleClick}
					open={open}
					values={values}
					setFieldValue={setFieldValue}
					className={className}
					cySelectionTestRef={cySelectionTestRef}
				/>
			)}
		</>
	);
};

export default InputSelect;
